import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  Download, X, ChevronLeft, ChevronRight,
  Images, ImageOff, ZoomIn, Search,
} from "lucide-react";
import "./Medialibrary.css";

export default function MediaLibrary() {
  const [events, setEvents]                   = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [vendors, setVendors]                 = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [eventsLoading, setEventsLoading]     = useState(true);
  const [query, setQuery]                     = useState("");

  // Lightbox
  const [lightbox, setLightbox] = useState(null); // { vendor, photos, index }

  // Per-vendor zip downloading
  const [zipping, setZipping] = useState({}); // { vendorId: true }

  /* =========================================================
     FETCH EVENTS
  ========================================================= */
  useEffect(() => {
    const fetchEvents = async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, start_date")
        .order("start_date", { ascending: false });

      if (error) { console.error(error); setEventsLoading(false); return; }

      setEvents(data || []);
      const global = data?.find(e => e.name?.toLowerCase().includes("global"));
      if (global || data?.[0]) setSelectedEventId((global || data[0]).id);
      setEventsLoading(false);
    };
    fetchEvents();
  }, []);

  /* =========================================================
     FETCH VENDORS + MEDIA FOR SELECTED EVENT
  ========================================================= */
  useEffect(() => {
    if (!selectedEventId) return;
    fetchMedia();
  }, [selectedEventId]);

  const fetchMedia = async () => {
    setLoading(true);
    setVendors([]);
    setQuery("");

    try {
      const { data: apps, error } = await supabase
        .from("vendor_applications")
        .select(`
          id,
          product_photo_paths,
          vendors ( id, brand_name, logo_path, logo_url )
        `)
        .eq("event_id", selectedEventId)
        .eq("status", "approved");

      if (error) throw error;

      const resolved = await Promise.all(
        (apps || []).map(async (app) => {
          const vendor = app.vendors;
          if (!vendor) return null;

          let logoUrl = null;
          if (vendor.logo_path) {
            const { data } = supabase.storage
              .from("logos")
              .getPublicUrl(vendor.logo_path);
            logoUrl = data?.publicUrl || null;
          }

          const photos = await Promise.all(
            (app.product_photo_paths || []).map(async (item) => {
              const { data } = await supabase.storage
                .from("product_photos")
                .createSignedUrl(item.path, 60 * 60);
              return {
                path: item.path,
                name: item.name,
                url:  data?.signedUrl || null,
              };
            })
          );

          return {
            applicationId: app.id,
            vendorId:      vendor.id,
            brandName:     vendor.brand_name,
            logoPath:      vendor.logo_path,
            logoUrl,
            photos:        photos.filter(p => p.url),
          };
        })
      );

      setVendors(resolved.filter(Boolean));
    } catch (err) {
      console.error("Media fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     FILTERED VENDORS
  ========================================================= */
  const filtered = query.trim()
    ? vendors.filter(v =>
        v.brandName?.toLowerCase().includes(query.trim().toLowerCase())
      )
    : vendors;

  /* =========================================================
     ZIP DOWNLOAD — single vendor
  ========================================================= */
  const handleZipDownload = async (vendor) => {
    setZipping(prev => ({ ...prev, [vendor.vendorId]: true }));

    try {
      const zip    = new JSZip();
      const folder = zip.folder(vendor.brandName);

      if (vendor.logoPath) {
        const { data: logoBlob, error } = await supabase.storage
          .from("logos")
          .download(vendor.logoPath);
        if (!error && logoBlob) {
          const ext = vendor.logoPath.split(".").pop();
          folder.file(`logo.${ext}`, logoBlob);
        }
      }

      for (const photo of vendor.photos) {
        const { data: blob, error } = await supabase.storage
          .from("product_photos")
          .download(photo.path);
        if (!error && blob) folder.file(photo.name, blob);
      }

      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `${vendor.brandName}_media.zip`);
    } catch (err) {
      console.error("Zip error:", err);
      alert("Failed to create zip: " + err.message);
    } finally {
      setZipping(prev => ({ ...prev, [vendor.vendorId]: false }));
    }
  };

  /* =========================================================
     ZIP DOWNLOAD — all vendors
  ========================================================= */
  const handleZipAll = async () => {
    setZipping(prev => ({ ...prev, all: true }));

    try {
      const zip = new JSZip();

      for (const vendor of vendors) {
        const folder = zip.folder(vendor.brandName);

        if (vendor.logoPath) {
          const { data: blob } = await supabase.storage
            .from("logos")
            .download(vendor.logoPath);
          if (blob) {
            const ext = vendor.logoPath.split(".").pop();
            folder.file(`logo.${ext}`, blob);
          }
        }

        for (const photo of vendor.photos) {
          const { data: blob } = await supabase.storage
            .from("product_photos")
            .download(photo.path);
          if (blob) folder.file(photo.name, blob);
        }
      }

      const eventName = events.find(e => e.id === selectedEventId)?.name || "event";
      const content   = await zip.generateAsync({ type: "blob" });
      saveAs(content, `${eventName}_all_media.zip`);
    } catch (err) {
      console.error("Zip all error:", err);
      alert("Failed to create zip: " + err.message);
    } finally {
      setZipping(prev => ({ ...prev, all: false }));
    }
  };

  /* =========================================================
     LIGHTBOX
  ========================================================= */
  const openLightbox  = (vendor, index) => setLightbox({ vendor, index });
  const closeLightbox = () => setLightbox(null);

  const lightboxNext = useCallback(() => {
    if (!lightbox) return;
    setLightbox(prev => ({
      ...prev,
      index: (prev.index + 1) % prev.vendor.photos.length,
    }));
  }, [lightbox]);

  const lightboxPrev = useCallback(() => {
    if (!lightbox) return;
    setLightbox(prev => ({
      ...prev,
      index: (prev.index - 1 + prev.vendor.photos.length) % prev.vendor.photos.length,
    }));
  }, [lightbox]);

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") lightboxNext();
      if (e.key === "ArrowLeft")  lightboxPrev();
      if (e.key === "Escape")     closeLightbox();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, lightboxNext, lightboxPrev]);

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="ml-page">

      {/* ── Page header ── */}
      <div className="ml-header">
        <div>
          <h1 className="ml-title">Media Library</h1>
          <p className="ml-subtitle">Logos and product photos from approved vendors</p>
        </div>

        <div className="ml-header-right">
          {!eventsLoading && events.length > 0 && (
            <select
              className="ml-event-select"
              value={selectedEventId || ""}
              onChange={e => setSelectedEventId(e.target.value)}
            >
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>
                  {ev.name}{ev.start_date ? ` · ${new Date(ev.start_date).getFullYear()}` : ""}
                </option>
              ))}
            </select>
          )}

          {vendors.length > 0 && (
            <button
              className="ml-btn ml-btn--primary"
              onClick={handleZipAll}
              disabled={zipping.all}
            >
              <Download size={15} />
              {zipping.all ? "Zipping…" : `Download All (${vendors.length})`}
            </button>
          )}
        </div>
      </div>

      {/* ── Search + stats bar ── */}
      {!loading && vendors.length > 0 && (
        <div className="ml-toolbar">
          <div className="ml-search-wrap">
            <Search size={14} className="ml-search-icon" />
            <input
              type="search"
              className="ml-search"
              placeholder="Search vendors…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            {query && (
              <button className="ml-search-clear" onClick={() => setQuery("")}>
                <X size={13} />
              </button>
            )}
          </div>

          <div className="ml-stats">
            {query ? (
              <>
                <span>{filtered.length} of {vendors.length} vendors</span>
                <span>·</span>
                <span>{filtered.reduce((s, v) => s + v.photos.length, 0)} photos</span>
              </>
            ) : (
              <>
                <span>{vendors.length} vendors</span>
                <span>·</span>
                <span>{vendors.filter(v => v.logoUrl).length} logos</span>
                <span>·</span>
                <span>{vendors.reduce((s, v) => s + v.photos.length, 0)} photos</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div className="ml-loading">
          <div className="ml-spinner" />
          <span>Loading media…</span>
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && vendors.length === 0 && (
        <div className="ml-empty">
          <ImageOff size={32} />
          <p>No media uploaded yet for this event.</p>
        </div>
      )}

      {/* ── No search results ── */}
      {!loading && vendors.length > 0 && filtered.length === 0 && (
        <div className="ml-empty">
          <Search size={28} />
          <p>No vendors match "<strong>{query}</strong>"</p>
          <button className="ml-btn" onClick={() => setQuery("")}>Clear search</button>
        </div>
      )}

      {/* ── Vendor cards grid ── */}
      {!loading && filtered.length > 0 && (
        <div className="ml-grid">
          {filtered.map(vendor => (
            <div key={vendor.vendorId} className="ml-card">

              <div className="ml-card-header">
                <div className="ml-card-brand">
                  {vendor.logoUrl ? (
                    <img
                      src={vendor.logoUrl}
                      alt={vendor.brandName}
                      className="ml-card-logo"
                    />
                  ) : (
                    <div className="ml-card-logo-placeholder">
                      {vendor.brandName?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div>
                    <h3 className="ml-card-name">
                      {query ? highlightMatch(vendor.brandName, query) : vendor.brandName}
                    </h3>
                    <span className="ml-card-count">
                      {vendor.photos.length} photo{vendor.photos.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                <button
                  className="ml-zip-btn"
                  onClick={() => handleZipDownload(vendor)}
                  disabled={zipping[vendor.vendorId]}
                  title="Download all as zip"
                >
                  <Download size={14} />
                  {zipping[vendor.vendorId] ? "…" : "Zip"}
                </button>
              </div>

              {vendor.photos.length > 0 ? (
                <div className="ml-thumbs">
                  {vendor.photos.map((photo, i) => (
                    <div
                      key={photo.path}
                      className="ml-thumb"
                      onClick={() => openLightbox(vendor, i)}
                    >
                      <img src={photo.url} alt={photo.name} loading="lazy" />
                      <div className="ml-thumb-overlay">
                        <ZoomIn size={16} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ml-no-photos">No product photos yet</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Lightbox ── */}
      {lightbox && (
        <div className="ml-lightbox-backdrop" onClick={closeLightbox}>
          <div className="ml-lightbox" onClick={e => e.stopPropagation()}>

            <div className="ml-lightbox-header">
              <div className="ml-lightbox-brand">
                {lightbox.vendor.logoUrl && (
                  <img src={lightbox.vendor.logoUrl} alt="" className="ml-lightbox-logo" />
                )}
                <span>{lightbox.vendor.brandName}</span>
              </div>
              <div className="ml-lightbox-actions">
                <a
                  href={lightbox.vendor.photos[lightbox.index].url}
                  download={lightbox.vendor.photos[lightbox.index].name}
                  className="ml-lightbox-dl"
                  title="Download this photo"
                >
                  <Download size={16} />
                </a>
                <button className="ml-lightbox-close" onClick={closeLightbox}>
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="ml-lightbox-img-wrap">
              {lightbox.vendor.photos.length > 1 && (
                <button className="ml-lightbox-nav ml-lightbox-nav--prev" onClick={lightboxPrev}>
                  <ChevronLeft size={22} />
                </button>
              )}

              <img
                src={lightbox.vendor.photos[lightbox.index].url}
                alt={lightbox.vendor.photos[lightbox.index].name}
                className="ml-lightbox-img"
              />

              {lightbox.vendor.photos.length > 1 && (
                <button className="ml-lightbox-nav ml-lightbox-nav--next" onClick={lightboxNext}>
                  <ChevronRight size={22} />
                </button>
              )}
            </div>

            <div className="ml-lightbox-footer">
              <span className="ml-lightbox-counter">
                {lightbox.index + 1} / {lightbox.vendor.photos.length}
              </span>
              <span className="ml-lightbox-filename">
                {lightbox.vendor.photos[lightbox.index].name}
              </span>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

/* =========================================================
   HIGHLIGHT MATCH HELPER
========================================================= */
function highlightMatch(text, query) {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "#fff3cd", color: "inherit", borderRadius: "2px", padding: "0 1px" }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}